<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import {
  showConfirmDialog,
  showLoadingToast,
  showSuccessToast,
  SwipeInstance,
} from "vant";
import { computed, nextTick, onMounted, ref } from "vue";
import { useTask } from "@/stores/task";
import { useRoute, useRouter } from "vue-router";
import InpaintingEditor, {
  InpaintingEditorInstance,
} from "@/components/mobile/generation/inpaint/InpaintingEditor.vue";
import { InpaintType } from "@/types/entity";
import api from "@/modules/api";
import { usePreviewImg, useRawImageUrl } from "@/use/common";
import { AxiosError } from "axios";
import { Response } from "@/types/api-types";
import { CustomPaintingEditorInstance } from "@/components/mobile/generation/custom-painting/CustomPaintingEditor.vue";
import i18n from "@/locales";
import { base64ToFile } from "@/modules/common";
import { ApiGetImageInpaint, ApiPostImageInpaint } from "@/apis/paint";
import { useI18n } from "vue-i18n";
import { useGlobalUser } from "@/stores/globalUser";
import NSFWModalPowerNotEnough from "@/views/NSFW/Modal/NSFWModalPowerNotEnough.vue";
import NSFWModalCrystalNotEnough from "@/views/NSFW/Modal/NSFWModalCrystalNotEnough.vue";
interface InpaintItem {
  media: Media;
  maskBase64?: string;
  promptText?: string;
}

const PAINTING_MODE = [
  i18n.global.t("Image.partialRedraw"),
  i18n.global.t("Image.fusionRedraw"),
];
const VALID_INPAINT_TYPE: { type: InpaintType; name: string }[] = [
  // {
  //   type: 1,
  //   name: i18n.global.t("Image.redraw.handRepair"),
  // },
  {
    type: 2,
    name: i18n.global.t("Image.redraw.addObject"),
  },
  // {
  //   type: 3,
  //   name: i18n.global.t("Image.redraw.detailRepair"),
  // },
  {
    type: 4,
    name: i18n.global.t("Image.redraw.deleteObject"),
  },
  {
    type: 5,
    name: i18n.global.t("Image.redraw.changeClothes"),
  },
  {
    type: 7,
    name: i18n.global.t("Image.redraw.changeExpression"),
  },
];

// State
const { t } = useI18n();
const taskStore = useTask();
const route = useRoute();
const router = useRouter();
const userStore = useGlobalUser();
// 基础数据
const tabIdx = ref<number>(0);
const taskId = Number(route.query.id);
const targetTask = computed(() => taskStore.getTask(taskId));
const loading = ref(true);
const inpaintingEditorRef = ref<InpaintingEditorInstance | null>(null);
const customPaintingEditorRef = ref<CustomPaintingEditorInstance | null>(null);
// 模式选择
const type = ref<InpaintType>(1);
// 选区遮罩层
const showMask = ref(true);
const maskBase64 = ref(""); // 实际给服务端的选区（没有透明度）
const maskMedia = ref<Media>();
// 重绘结果缓存
const inpaintList = ref<InpaintItem[]>([]); // 重绘结果
const previewList = computed<InpaintItem[]>(() => {
  const media = targetTask.value?.entity?.media;
  if (!media) return [];
  return [{ media }, ...inpaintList.value];
});
const previewIdx = ref(0);
const selectedPreviewItem = computed(() => previewList.value[previewIdx.value]);
const swipeRef = ref<SwipeInstance | null>(null);
// 提示词
const showPromptPopup = ref(false);
const promptInputText = ref("");
const confirmedText = ref("");
const isUnlimited = ref(false);

// Lifecycle
onMounted(async () => {
  loading.value = true;
  try {
    const task = await taskStore.getTaskOrFetch(taskId);
    isUnlimited.value = !!(task?.entity.media?.isNsfw || task?.entity.isNsfw);
  } finally {
    loading.value = false;
    nextTick(() => {
      swipeRef.value?.resize();
    });
  }
});

// Event Handler
const selectInpaintingRegion = () => {
  inpaintingEditorRef.value?.start(selectedPreviewItem.value.media.url);
};
const startCustomInpainting = () => {
  customPaintingEditorRef.value?.start(
    selectedPreviewItem.value.media.url,
    (targetTask.value?.entity as TextImage).style
  );
};
let loadingInpaint: any;
const showInsufficientCrystalPopup = ref(false);
const showInsufficientPowerPopup = ref(false);
const startInpainting = async () => {
  if (isUnlimited.value && Number(userStore.user.crystalQuantity) < 0.5)
    return (showInsufficientCrystalPopup.value = true);
  if (!isUnlimited.value && Number(userStore.user.powerAmount) < 1)
    return (showInsufficientPowerPopup.value = true);

  loadingInpaint = showLoadingToast({
    message: i18n.global.t("Message.redrawing"),
  });
  try {
    const res = await ApiPostImageInpaint({
      sourceMediaId: selectedPreviewItem.value.media.id!,
      maskMediaId: maskMedia.value?.id!,
      prompt: confirmedText.value,
      type: type.value === 4 ? 4 : 2,
      strength: inpaintingRange.value,
      isUnlimited: isUnlimited.value,
    });
    userStore.updateCurrentMateId();
    queryInpaintingResult(res.item.uuid);
  } catch (e) {
    loadingInpaint.close();
  }
};

const queryInpaintingResult = async (uuid: string) => {
  const res = await ApiGetImageInpaint(uuid);
  if (res.item.media) {
    res.item.media.url = useRawImageUrl(res.item.media.url);
    inpaintList.value.push({ media: res.item.media });
    onPreviewIdxChange(previewList.value.length - 1);
    showMask.value = false;
    loadingInpaint.close();
  } else setTimeout(() => queryInpaintingResult(uuid), 3000);
};

const onFinishedRegionSelection = async (params: { base64: string }) => {
  maskBase64.value = params.base64;
  const file = base64ToFile(params.base64, "mask.png");
  const loading = showLoadingToast(i18n.global.t("Message.loading"));
  const res = await api.uploadMedia(5, file);
  maskMedia.value = res.item;
  loading.close();
  showMask.value = true;
};

const onOpenLimited = () => {
  if (isUnlimited.value && targetTask.value?.entity.media?.isNsfw)
    return showConfirmDialog({
      message: t("painting.originIsNsfwCannotCloseNsfw"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });

  isUnlimited.value = !isUnlimited.value;
};

const onFinishedCustomInpainting = (media: Media) => {
  inpaintList.value.push({ media });
  onPreviewIdxChange(previewList.value.length - 1);
  showMask.value = false;
};

const onReplaceMedia = async () => {
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.covering"),
  });
  try {
    const { uuid, isNotShown } = targetTask.value!.entity!;
    const sourceMediaId = selectedPreviewItem.value.media.id;
    await api.updateTextImage(uuid, {
      mediaId: sourceMediaId!,
      isNotShown: !!isNotShown,
    });
    await taskStore.getTaskOrFetch(taskId, true);
    loadingToast.close();
    showSuccessToast({ message: i18n.global.t("Message.coverageSuccessful") });
    router.back();
  } finally {
    loadingToast.close();
  }
};

const onSaveGallery = async () => {
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.characterAlbum"),
  });
  try {
    const mediaId = selectedPreviewItem.value.media.id;
    await api.createOneMateMedia({ mediaId: mediaId!, type: 1 });
    loadingToast.close();
    showSuccessToast({ message: i18n.global.t("Message.saveSuccess") });
  } catch (e) {
    const error = e as AxiosError<Response<never>, never>;
    if (error.response?.data?.message === "duplicated media") {
      loadingToast.close();
      showSuccessToast({ message: i18n.global.t("Message.savedAlbum") });
    }
  } finally {
    loadingToast.close();
  }
};

function onPreviewIdxChange(idx: number) {
  previewIdx.value = idx;
  swipeRef.value?.swipeTo(previewIdx.value, { immediate: true });
}

const inpaintingRange = ref(75);

const showFacePromptModal = ref(false);

const clickPrompt = () => {
  promptInputText.value = confirmedText.value;
  if (type.value === 7) {
    showFacePromptModal.value = true;
  } else {
    showPromptPopup.value = true;
  }
};

const prompt = ref("");
const clickFacePromptConfirm = async () => {
  confirmedText.value = prompt.value;
  showFacePromptModal.value = false;
};

const customStrengthMap = {
  1: 75,
  3: 75,
  7: 25,
};

const clickSwitchMap = (_type: InpaintType) => {
  type.value = _type;
  inpaintingRange.value = customStrengthMap[_type as 1 | 3 | 7] || 75;
};

const onStrengthUpdate = (value: number) => {
  customStrengthMap[type.value as 1 | 3 | 7] = value;
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto] overflow-hidden">
    <threads-nav
      :back-double-check-message="$t('Message.exitRedrawnImage')"
      :show-center="false"
      back-double-check
      show-back
    >
      <template #right>
        <button
          :disabled="inpaintList.length === 0"
          class="btn btn-sm"
          @click="onSaveGallery"
        >
          {{ $t("Buttons.savedAlbum") }}
        </button>
        <button
          :disabled="inpaintList.length === 0"
          class="btn btn-sm btn-primary"
          @click="onReplaceMedia"
        >
          {{ $t("Buttons.coverOriginalImage") }}
        </button>
      </template>
    </threads-nav>
    <div v-if="loading" class="flex justify-center items-center">
      <div class="loading"></div>
    </div>
    <div
      v-else
      class="h-full grid grid-rows-[1fr_auto_auto] place-items-center overflow-hidden"
    >
      <!--  图片 + 提示词 + 预览列表  -->
      <div class="w-full h-full overflow-hidden grid grid-rows-[1fr_auto_auto]">
        <div class="w-full h-full relative overflow-hidden">
          <VanSwipe
            ref="swipeRef"
            :loop="false"
            class="w-full h-full bg-base-200"
            indicator-color="white"
            @change="
              (idx:number) => {
                previewIdx = idx;
              }
            "
          >
            <VanSwipeItem
              v-for="item in previewList"
              :key="item.media.url"
              class="relative"
            >
              <VanImage
                :src="usePreviewImg(item.media.url)"
                class="w-full h-full pointer-events-none"
                fit="scale-down"
                crossorigin="anonymous"
              />
              <VanImage
                v-show="maskBase64 && showMask && tabIdx === 0"
                :src="maskBase64"
                class="absolute top-0 left-0 right-0 bottom-0 bg-white/20 opacity-40 pointer-events-none"
                fit="scale-down"
                crossorigin="anonymous"
                @load="loading = false"
              />
            </VanSwipeItem>
            <template #indicator="{ active, total }">
              <div
                class="absolute left-2 bottom-2 bg-white/10 p-1 rounded text-xs"
              >
                {{ active + 1 }} / {{ total }}
              </div>
            </template>
          </VanSwipe>
          <div
            v-if="maskBase64 && tabIdx === 0"
            class="absolute right-2 bottom-2"
          >
            <button class="btn btn-xs" @click="showMask = !showMask">
              <i v-show="!showMask" class="iconfont icon-eye-regular"></i>
              <i v-show="showMask" class="iconfont icon-eye-slash"></i>
            </button>
          </div>
        </div>
        <div
          v-show="confirmedText"
          class="w-full p-2 bg-neutral max-h-20 overflow-y-auto"
        >
          {{ confirmedText }}
        </div>
        <div class="w-full border overflow-x-auto rounded-bl rounded-br">
          <div class="flex h-full">
            <img
              v-for="(item, idx) in previewList"
              :key="item.media.id"
              :class="{
                'border-primary': idx === previewIdx,
                'border-transparent': idx !== previewIdx,
              }"
              :src="usePreviewImg(item.media.url)"
              alt="preview"
              class="h-12 md:h-16 border shrink-0 w-auto"
              @click="onPreviewIdxChange(idx)"
              crossorigin="anonymous"
            />
          </div>
        </div>
      </div>
      <div class="flex flex-col w-full py-2">
        <div class="tabs tabs-xs tab-lifted border-primary" role="tablist">
          <div
            v-for="(item, idx) in PAINTING_MODE"
            :key="idx"
            :class="{ 'tab-active': tabIdx === idx }"
            class="tab tab-sm"
            role="tab"
            @click="tabIdx = idx"
          >
            {{ item }}
          </div>
        </div>
        <div
          v-show="tabIdx === 0"
          class="flex flex-col items-center justify-center gap-3 p-4"
        >
          <div class="flex justify-center items-center w-full">
            <div class="tabs tabs-boxed w-fit" role="tablist">
              <div
                v-for="item in VALID_INPAINT_TYPE"
                :key="item.type"
                :class="{ 'tab-active': type === item.type }"
                class="tab tab-sm"
                role="tab"
                @click="clickSwitchMap(item.type)"
              >
                {{ item.name }}
              </div>
            </div>
          </div>
          <div class="flex flex-col items-center w-full gap-2">
            <div
              v-if="[1, 3, 7].includes(type)"
              class="flex items-center gap-4 h-6"
            >
              <span>{{ $t("Texts.redrawWidth") }}</span>
              <VanSlider
                v-model="inpaintingRange"
                :max="100"
                :min="0"
                :step="5"
                class="w-24"
                size="sm"
                @change="onStrengthUpdate"
              >
                <template #button>
                  <button class="btn btn-circle btn-xs">
                    {{ inpaintingRange }}
                  </button>
                </template>
              </VanSlider>
            </div>
            <div v-else class="h-6"></div>
            <div class="flex gap-1 items-center">
              <div v-if="!userStore.isCNUser">
                <i class="iconfont icon-heart-fill text-[#F75ECC] text-2xl"></i>

                <VanSwitch
                  :model-value="isUnlimited"
                  @update:model-value="onOpenLimited"
                  active-color="#F75ECC"
                  size="14px"
                />
              </div>
              <span>
                {{ $t("Texts.redrawBasedTh", { index: previewIdx + 1 }) }}
              </span>
            </div>
          </div>
          <div class="flex gap-2">
            <button
              :class="[
                'btn btn-sm flex-1',
                maskBase64 ? ' px-1' : 'btn-primary',
              ]"
              @click="selectInpaintingRegion"
            >
              {{ $t("Buttons.selectRedrawArea") }}
            </button>
            <button
              v-show="maskBase64"
              class="btn btn-sm flex-1"
              @click="clickPrompt"
            >
              {{ $t("Buttons.addPrompt") }}
            </button>
            <button
              v-show="maskBase64"
              class="btn btn-sm btn-primary flex-1"
              @click="startInpainting"
            >
              <div class="flex gap-1 items-center">
                {{ $t("Buttons.startRedrawing") }}
                <div v-if="isUnlimited" class="flex gap-1 items-center">
                  <CrystalIcon />
                  <span>0.5</span>
                </div>
                <div v-else class="flex gap-1 items-center">
                  <PowerIcon />
                  <span>1</span>
                </div>
              </div>
            </button>
          </div>
        </div>
        <div
          v-show="tabIdx === 1"
          class="flex flex-col items-center justify-center gap-3 p-4"
        >
          <div class="flex flex-col w-full items-center gap-2">
            <div>
              {{ $t("Texts.redrawBasedTh", { index: previewIdx + 1 }) }}
            </div>
          </div>
          <button class="btn btn-sm btn-primary" @click="startCustomInpainting">
            {{ $t("Buttons.goToRepaintCanvas") }}
          </button>
        </div>
      </div>
    </div>
    <inpainting-editor
      ref="inpaintingEditorRef"
      @on-finished="onFinishedRegionSelection"
    />
    <custom-painting-editor
      ref="customPaintingEditorRef"
      @on-finished="onFinishedCustomInpainting"
    />

    <VanPopup
      v-model:show="showFacePromptModal"
      class="grid grid-rows-[auto_1fr] overflow-hidden h-[90%]"
      position="bottom"
      round
      teleport="body"
    >
      <threads-nav>
        <template #left>
          <div></div>
        </template>
        <template #center>{{ $t("Texts.redrawPromptWord") }}</template>
        <template #right>
          <button
            class="btn btn-primary btn-sm"
            @click="clickFacePromptConfirm"
          >
            {{ $t("Buttons.sure") }}
          </button>
        </template>
      </threads-nav>
      <edit-prompts
        v-model:prompt="prompt"
        mode="face"
        :show-negative="false"
        class="overflow-hidden"
      />
    </VanPopup>
    <VanPopup
      v-model:show="showPromptPopup"
      class="max-h-[50%] grid grid-rows-[auto_1fr] overflow-hidden"
      position="bottom"
      round
      teleport="body"
    >
      <threads-nav>
        <template #left>
          <div></div>
        </template>
        <template #center>{{ $t("Texts.redrawPromptWord") }}</template>
        <template #right>
          <button class="btn btn-ghost btn-sm" @click="promptInputText = ''">
            {{ $t("Buttons.clear") }}
          </button>
          <button
            class="btn btn-primary btn-sm"
            @click="
              confirmedText = promptInputText;
              showPromptPopup = false;
            "
          >
            {{ $t("Buttons.confirm") }}
          </button>
        </template>
      </threads-nav>
      <div>
        <VanField
          v-model="promptInputText"
          :placeholder="$t('Texts.pleaseEnterPromptWord')"
          autosize
          class="after:hidden overflow-auto"
          rows="8"
          type="textarea"
        />
      </div>
    </VanPopup>

    <NSFWModalCrystalNotEnough v-model:show="showInsufficientCrystalPopup" />
    <NSFWModalPowerNotEnough
      v-model:show="showInsufficientPowerPopup"
      :miss-amount="20"
    />
  </div>
  <div id="mergeImageDOM" class="w-0 h-0 opacity-0"></div>
</template>
